import { useEffect, useState } from 'react';
import { Loader2, Workflow, X } from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { supabase } from '../../lib/supabase';
import PostmarkSignatureSelect from '../PostmarkSignatures/PostmarkSignatureSelect';

interface CreateAutomationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (automationId: string) => void;
}

export default function CreateAutomationModal({
  isOpen,
  onClose,
  onCreated,
}: CreateAutomationModalProps) {
  const [name, setName] = useState('');
  const [postmarkSignatureId, setPostmarkSignatureId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setPostmarkSignatureId(null);
    setCreating(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !creating) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, creating, onClose]);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Name is required');
      return;
    }
    setCreating(true);
    try {
      const { data, error } = await supabase
        .from('automations')
        .insert({
          name: trimmed,
          status: 'draft',
          postmark_signature_id: postmarkSignatureId,
        })
        .select('id')
        .single();
      if (error) throw error;
      onCreated(data.id as string);
      onClose();
    } catch (e: unknown) {
      reportUiError('automations', 'create', e);
      toast.error(e instanceof Error ? e.message : 'Failed to create automation');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => {
        if (!creating) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-automation-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2
            id="create-automation-title"
            className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
          >
            <Workflow className="w-5 h-5 text-emerald-500" />
            New automation
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Name the sequence and choose who emails send from. You can add steps on the next
            screen.
          </p>

          <div>
            <label
              htmlFor="create-automation-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="create-automation-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Welcome series"
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <PostmarkSignatureSelect
            value={postmarkSignatureId}
            onChange={setPostmarkSignatureId}
            disabled={creating}
          />
        </div>

        <div className="shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={creating || !name.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {creating ? 'Creating…' : 'Create automation'}
          </button>
        </div>
      </div>
    </div>
  );
}
